import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { ActionButtonComponent } from "../../shared/action-button/action-button.component";
import { ApiService } from "src/app/api.service";
import { DvsUtilities } from "src/app/dvs-utilities";
import { APIResults } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-fleet-management",
	templateUrl: "./fleet-management.component.html",
	styleUrls: ["./fleet-management.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormsModule, ActionButtonComponent]
})
export class FleetManagementComponent implements OnInit {
	api = inject(ApiService);

	@ViewChild("userPanel") userPanel: any;
	@ViewChild("assetPanel") assetPanel: any;
	@ViewChild("fleetPanel") fleetPanel: any;

	public brand = BrandVars;

	organization?: APIResults.Organization;
	organizations: APIResults.Organization[];
	users: APIResults.User[];
	selecteduser?: APIResults.User;
	showadduser = false;
	adduser = {
		email: "",
		password: "",
		role: "user"
	};
	newemail = "";
	newpassword = "";
	newrole = "";

	assets: APIResults.Asset[];
	selectedasset?: APIResults.Asset;
	assetPartners: string[];
	updateasset: any = {};

	fleets: APIResults.Fleet[];
	selectedfleet?: APIResults.Fleet;
	showaddfleet = false;
	addfleetname = "";
	addusererrormessage = undefined;
	addfleeterrormessage = undefined;
	updatefleeterrormessage = undefined;

	configs: APIResults.AssetConfig[];

	async ngOnInit() {
		if (this.api.user && this.api.user.organization_name) {
			const orgid = this.api.user.organization_id;
			this.organization = {
				organization_id: orgid,
				name: this.api.user.organization_name,
			};
			this.RefreshUsers();
			this.RefreshAssets();
			this.RefreshFleets();
			this.RefreshConfigs();
			if (this.api.user.role === "admin" || this.api.user.role === "master") {
				// Admins get to select any organization to masquerade as
				this.organizations = await this.api.GetOrganizations();
				const neworg = this.organizations.find(org => org.organization_id === orgid);
				this.organization = neworg;
			}
		}
	}

	SelectOrganization(org: APIResults.Organization) {
		this.organization = org;
		this.RefreshUsers();
		this.RefreshAssets();
		this.RefreshFleets();
		this.RefreshConfigs();
	}

	private scrollToSection(child: any) {
		child?.nativeElement?.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
	}

	// Users
	SelectUser(user: APIResults.User) {
		this.selecteduser = user;
		this.scrollToSection(this.userPanel);
	}

	async RefreshUsers() {
		if (this.organization) {
			this.users = await this.api.GetUsers(this.organization.organization_id);
			if (this.selecteduser) {
				const userid = this.selecteduser.user_id;
				const newselect = this.users.find(user => user.user_id === userid);
				this.selecteduser = newselect;
			}
		}
	}

	async AddUser() {
		this.addusererrormessage = undefined;
		if (this.organization) {
			try {
				await this.api.AddUser(this.organization.organization_id, this.adduser.email, this.adduser.password, this.adduser.role);
				this.adduser = {
					email: "",
					password: "",
					role: "user"
				};
				await this.RefreshUsers();
			} catch (err) {
				this.addusererrormessage = DvsUtilities.GetRemoteErrorMessage(err, "Couldn't add user");
			}
		}
	}

	async SetPassword() {
		if (!this.newpassword) {
			throw new Error("Password required");
		}
		if (this.organization && this.selecteduser) {
			await this.api.ChangeUser(this.organization.organization_id, this.selecteduser.user_id, undefined, this.newpassword);
			this.newpassword = "";
		}
	}

	async RenameUser() {
		if (!this.newemail) {
			throw new Error("Email required");
		}
		if (this.organization && this.selecteduser) {
			await this.api.ChangeUser(this.organization.organization_id, this.selecteduser.user_id, this.newemail);
			this.newemail = "";
		}
	}

	async UpdateUser() {
		if (this.organization && this.selecteduser) {
			await this.api.ChangeUser(this.organization.organization_id, this.selecteduser.user_id, undefined, undefined, this.newrole, this.selecteduser.hide_rtb, this.selecteduser.hide_fdm, this.selecteduser.hide_mech);
			await this.RefreshUsers();
		}
	}

	async SendEmail() {
		if (this.organization && this.selecteduser) {
			await this.api.SendTestEmail(this.organization.organization_id, this.selecteduser.user_id);
		}
	}

	async DeleteUser() {
		if (this.organization && this.selecteduser) {
			await this.api.DeleteUser(this.organization.organization_id, this.selecteduser.user_id);
			await this.RefreshUsers();
		}
	}

	async DisableMFA() {
		if (this.organization && this.selecteduser) {
			await this.api.AdminDisableMFA(this.organization.organization_id, this.selecteduser.user_id);
			await this.RefreshUsers();
		}
	}

	// Assets
	SelectAsset(asset: APIResults.Asset) {
		this.selectedasset = asset;
		this.updateasset = JSON.parse(JSON.stringify(asset));
		this.updateasset.config = this.configs.find(item => item.asset_type_id === asset.asset_type_id);
		this.updateasset.fleet = this.fleets.find(item => item.fleet_id === asset.fleet_id);
		this.assetPartners = undefined;
		this.api.GetAssetPartnerNames(asset.asset_id).then(partners => this.assetPartners = partners);
		this.scrollToSection(this.assetPanel);
	}

	async RefreshAssets() {
		if (this.organization) {
			this.assets = await this.api.GetAssets(this.organization.organization_id);
			if (this.selectedasset) {
				const assetid = this.selectedasset.asset_id;
				const newselect = this.assets.find(asset => asset.asset_id === assetid);
				this.selectedasset = newselect;
			}
		}
	}

	async UpdateAsset() {
		const params = {
			fleet_id: this.updateasset.fleet.fleet_id,
			asset_name: this.updateasset.asset_name,
			asset_type_id: this.updateasset.config.asset_type_id,
			flight_time_offset: this.updateasset.flight_time_offset,
			severe_exceedances_only: !!this.updateasset.severe_exceedances_only,
		};
		await this.api.UpdateAsset(this.updateasset.organization_id, this.updateasset.asset_id, params);
		await this.RefreshAssets();
	}

	// Fleets
	SelectFleet(fleet: APIResults.Fleet) {
		this.selectedfleet = fleet;
		this.scrollToSection(this.fleetPanel);
	}

	async RefreshFleets() {
		if (this.organization) {
			this.fleets = await this.api.GetOrgFleets(this.organization.organization_id);
			if (this.selectedfleet) {
				const fleetid = this.selectedfleet.fleet_id;
				const newselect = this.fleets.find(fleet => fleet.fleet_id === fleetid);
				this.selectedfleet = newselect;
			}
		}
	}

	async AddFleet() {
		this.addfleeterrormessage = undefined;
		try {
			if (this.organization) {
				if (this.addfleetname.includes("_")) {
					throw new Error("Name cannot include underscores (_)");
				}
				await this.api.AddOrgFleet(this.organization.organization_id, this.addfleetname);
				this.addfleetname = "";
				await this.RefreshFleets();
			}
		} catch (err) {
			this.addfleeterrormessage = DvsUtilities.GetRemoteErrorMessage(err, "Couldn't add fleet");
		}
	}

	async UpdateFleet() {
		this.updatefleeterrormessage = undefined;
		try {
			if (this.organization && this.selectedfleet) {
				await this.api.ChangeOrgFleet(this.organization.organization_id, this.selectedfleet.fleet_id, this.selectedfleet.time_offset_hours);
				await this.RefreshFleets();
			}
		} catch (err) {
			this.updatefleeterrormessage = DvsUtilities.GetRemoteErrorMessage(err, "Couldn't update fleet");
		}
	}

	async DeleteFleet() {
		if (this.organization && this.selectedfleet) {
			await this.api.DeleteFleet(this.organization.organization_id, this.selectedfleet.fleet_id);
			await Promise.all([
				this.RefreshFleets(),
				this.RefreshAssets(),
			]);
		}
	}

	// Configs
	async RefreshConfigs() {
		if (this.organization) {
			this.configs = await this.api.GetAssetConfigs(this.organization.organization_id);
		}
	}
}
